/**
 * Shared Mapbox DEM (terrain-dem-v1) tile machinery for the rest of the app.
 *
 * Two callers consume DEM data:
 *   1. LocalMap.tsx — clipping the satellite overlay against bathymetry.
 *      Wants ImageBitmap tiles to draw onto a canvas.
 *   2. DigitalElevationModel.ts — querying elevation per (lng, lat) for
 *      simulation grids. Wants point samples.
 *
 * Both share:
 *   - A captured sku token (required by mapbox.mapbox-terrain-dem-v1, which
 *     rejects plain pk.* access tokens with 401).
 *   - A per-tile cache of decoded bitmaps.
 *   - Parent-tile fallback when the requested zoom exceeds the dataset's
 *     coverage cap (z=14 for terrain-dem-v1).
 *
 * The sku is captured from any api.mapbox.com URL passing through the
 * fetch interceptor in LocalMap (Mapbox GL JS appends sku to every tile
 * request). The map's style triggers tile fetches as soon as the map
 * mounts, so the sku is available almost immediately at app startup —
 * well before any code path that needs it (simulation, etc.) can run.
 */

/**
 * A sub-region of a parent DEM tile, used when the requested zoom exceeds
 * the dataset's coverage. For a direct hit (no fallback), srcX = srcY = 0
 * and srcSize === parentSize.
 */
interface IDemSubTile {
  bitmap: ImageBitmap;
  /** Source rectangle within the parent bitmap. */
  srcX: number;
  srcY: number;
  srcSize: number;
  /** Parent tile dimensions in pixels (256 or 512). */
  parentSize: number;
  /** Parent (z, x, y) — used as a cache key for derived per-pixel data. */
  parentZ: number;
  parentX: number;
  parentY: number;
}

class MapboxDem {
  private static accessToken: string | null = null;
  private static sku: string | null = null;

  /** Per-tile cache of decoded DEM bitmaps. null entries record known 404s. */
  private static bitmapCache = new Map<string, Promise<ImageBitmap | null>>();

  /** Per-parent-tile cache of decoded RGB pixel data, for getElevation. */
  private static imageDataCache = new Map<string, Uint8ClampedArray>();

  /**
   * Lowest zoom level we've ever seen 404 at. Once known, fetches at or
   * above this zoom skip the doomed direct attempt and start at the
   * parent. Only updated on failure — successful fetches at low zoom
   * must not lower this (otherwise low-zoom usage would cap subsequent
   * high-zoom requests).
   */
  private static firstFailedZoom = Infinity;

  /** Set the Mapbox access token. Called once at app startup. */
  static setAccessToken(token: string) {
    MapboxDem.accessToken = token;
  }

  /**
   * Capture the sku token from a passing Mapbox URL.
   * Mapbox GL JS appends ?sku=… to every api.mapbox.com tile request;
   * the fetch interceptor in LocalMap calls this for every URL it sees so
   * we always have a fresh sku. terrain-dem-v1 requires sku — it 401s on
   * plain pk.* tokens.
   */
  static captureSkuFromUrl(url: string) {
    const match = url.match(/[?&]sku=([^&]+)/);
    if (match) MapboxDem.sku = match[1];
  }

  private static buildQueryString(): string {
    const parts: string[] = [];
    if (MapboxDem.sku) parts.push(`sku=${MapboxDem.sku}`);
    if (MapboxDem.accessToken) parts.push(`access_token=${MapboxDem.accessToken}`);
    return parts.join('&');
  }

  /**
   * Fetch one DEM tile as an ImageBitmap, with caching. Returns null on
   * non-OK HTTP status (typically 404 above the dataset's max zoom).
   */
  private static fetchBitmap(z: number, x: number, y: number): Promise<ImageBitmap | null> {
    const key = `${z}/${x}/${y}`;
    const cached = MapboxDem.bitmapCache.get(key);
    if (cached) return cached;

    const promise = (async (): Promise<ImageBitmap | null> => {
      const url = `https://api.mapbox.com/raster/v1/mapbox.mapbox-terrain-dem-v1/${z}/${x}/${y}.webp?${MapboxDem.buildQueryString()}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();
      return createImageBitmap(blob);
    })();
    MapboxDem.bitmapCache.set(key, promise);
    return promise;
  }

  /**
   * Fetch the DEM tile for (z, x, y), falling back up the parent chain on
   * 404. terrain-dem-v1's documented coverage stops at z=14, so any z=15+
   * request 404s; we silently pick up the z=14 parent and crop to the
   * requested child region.
   *
   * Returns null only when even z=0 has no data (shouldn't happen for
   * terrain-dem-v1).
   */
  static async getTile(z: number, x: number, y: number): Promise<IDemSubTile | null> {
    // Skip zooms we already know to be uniformly 404'd.
    const startZ = Math.min(z, MapboxDem.firstFailedZoom - 1);
    for (let pz = startZ; pz >= 0; pz--) {
      const dz = z - pz;
      const px = x >>> dz;
      const py = y >>> dz;
      const bitmap = await MapboxDem.fetchBitmap(pz, px, py);
      if (!bitmap) {
        if (pz < MapboxDem.firstFailedZoom) MapboxDem.firstFailedZoom = pz;
        continue;
      }
      const factor = 1 << dz;
      const parentSize = bitmap.width;
      const subSize = parentSize / factor;
      const srcX = (x & (factor - 1)) * subSize;
      const srcY = (y & (factor - 1)) * subSize;
      return {
        bitmap,
        srcX, srcY, srcSize: subSize,
        parentSize,
        parentZ: pz, parentX: px, parentY: py,
      };
    }
    return null;
  }

  /**
   * Lazily compute and cache the parent bitmap's RGB pixel data.
   * Used for per-pixel elevation queries (e.g. simulation grids).
   */
  private static async getImageData(tile: IDemSubTile): Promise<Uint8ClampedArray> {
    const key = `${tile.parentZ}/${tile.parentX}/${tile.parentY}`;
    const cached = MapboxDem.imageDataCache.get(key);
    if (cached) return cached;

    const canvas = document.createElement('canvas');
    canvas.width = tile.parentSize;
    canvas.height = tile.parentSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error("Failed to obtain 2D canvas context.");
    ctx.drawImage(tile.bitmap, 0, 0);
    const data = ctx.getImageData(0, 0, tile.parentSize, tile.parentSize).data;
    MapboxDem.imageDataCache.set(key, data);
    return data;
  }

  /**
   * Decode an elevation in meters from terrain-dem-v1's RGB encoding:
   *   height = -10000 + (R * 256² + G * 256 + B) * 0.1
   */
  static decodeElevation(r: number, g: number, b: number): number {
    return -10000 + ((r * 256 * 256 + g * 256 + b) * 0.1);
  }

  /**
   * Read the elevation in meters at a geographic coordinate, fetching
   * the DEM tile that contains it (with parent fallback when needed).
   * Returns null if no DEM data is available — typically because no sku
   * has been captured yet, e.g. before the user has interacted with the
   * map.
   */
  static async getElevation(lng: number, lat: number, zoom: number): Promise<number | null> {
    const numTiles = Math.pow(2, zoom);
    const hor = (lng + 180) / 360 * numTiles;
    const tileX = Math.floor(hor);
    const xFrac = hor - tileX;
    const latRad = lat * Math.PI / 180;
    const vert = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * numTiles;
    const tileY = Math.floor(vert);
    const yFrac = vert - tileY;

    const tile = await MapboxDem.getTile(zoom, tileX, tileY);
    if (!tile) return null;

    // Translate fractional coords inside the conceptual child tile to
    // absolute pixel coords inside the parent bitmap.
    const px = Math.floor(tile.srcX + xFrac * tile.srcSize);
    const py = Math.floor(tile.srcY + yFrac * tile.srcSize);

    const data = await MapboxDem.getImageData(tile);
    const offset = (py * tile.parentSize + px) * 4;
    return MapboxDem.decodeElevation(data[offset], data[offset + 1], data[offset + 2]);
  }
}

export { MapboxDem, IDemSubTile };
